import { connect, useDispatch } from 'react-redux';
import { Dispatch, useCallback } from 'react';
import { FieldArray } from 'react-final-form-arrays';

import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import {
  ADD_SUSPENSION_ERROR_BY_NOT_FILLED_ACT,
  ADD_SUSPENSION_ERROR_BY_NOT_SIGNED_ACT,
} from 'CorePackage/MainModule/constants/notices';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';

import { IRootState } from '../../CorePackage/MainModule/interfaces/IRootState';
import { IKnmFormRef } from '../../KnmPackage/MainModule/interfaces/IKnmFormRef';
import {
  getIsRsnCngPPXXII,
  getKnmFormRef,
  getKnmIsErknmStatusTypeCarriedOut,
  getKnmIsErknmStatusTypeWaitingCarryOut,
  getKnmIsNeedActSaveOrSign,
} from '../../KnmPackage/MainModule/selectors/knmSelectors';
import { FieldBlock } from '../../UIPackage/FormModule/components/FieldBlock';
import { Button } from '../../UIPackage/MainModule/components/Button';
import { IKnmForm } from '../../KnmPackage/MainModule/interfaces/IKnmForm';
import { IKnmErknmFormSuspensionEvent } from '../MainModule/interfaces/IKnmErknmFormSuspensionEvent';
import styles from './ErknmSuspensionEvents.module.scss';
import { getUserRoleIsSupervisor } from '../../AuthPackage/MainModule/selectors/authSelectors';
import { IKnmErknmFormEvent } from '../MainModule/interfaces/IKnmErknmFormEvent';
import { KnmKindCodesEnum } from '../../APIPackage/DictionariesModule/enums/KnmKindCodesEnum';
import { IKnmErknmFormEventSelectOption } from '../ErknmInfoModule/interfaces/IKnmErknmFormEventSelectOption';
import { getSuspensionEventBlockId } from '../MainModule/constants/knmErknmFieldsElementsIds';
import { createKnmErknmFormEventSelectOption } from '../../CorePackage/CatalogsModule/common/createKnmErknmFormEventSelectOption';
import { ErknmSuspensionEventItem } from './ErknmSuspensionEventItem';

interface IErknmSuspensionEventsStoreProps {
  formRef: IKnmFormRef | undefined;
  isSupervisor: boolean;
  isKnmStatusTypeWaitingCarryOut: boolean;
  isKnmStatusTypeCarriedOut: boolean;
  settings: IAdminSetting[];
  isNeedActSaveOrSign?: boolean;
  isRsnCngPPXXII: boolean;
}

interface IErknmSuspensionEventsProps {
  eventsErknm?: IKnmErknmFormEvent[];
  kindKnmCode: KnmKindCodesEnum | undefined;
  suspensionEvents?: IKnmErknmFormSuspensionEvent[];
  created: string | undefined;
}

function filterEventOptions(
  events: IKnmErknmFormEventSelectOption[],
  types: string[],
): IKnmErknmFormEventSelectOption[] {
  return events.filter(event => event.type && types.includes(event.type.title));
}

const ErknmSuspensionEventsWithoutStore = ({
  formRef,
  isSupervisor,
  isKnmStatusTypeCarriedOut,
  isKnmStatusTypeWaitingCarryOut,
  eventsErknm,
  kindKnmCode,
  suspensionEvents,
  settings,
  created,
  isNeedActSaveOrSign = false,
  isRsnCngPPXXII,
}: IErknmSuspensionEventsStoreProps & IErknmSuspensionEventsProps) => {
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const isValid412 = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_223_4_1_2_STARTS_FROM,
  );

  const isKindDP = kindKnmCode === KnmKindCodesEnum.DP;
  const eventOptions = eventsErknm?.map((value, index) => createKnmErknmFormEventSelectOption(value, index)) || [];
  const filteredEventOptions = filterEventOptions(
    eventOptions,
    ['Экспертиза', 'Испытание']
      .concat(isKindDP ? ['Истребование документов'] : [])
      .concat(isValid412 && isKindDP ? ['Получение письменных объяснений'] : []),
  );

  const suspensionEventsWithEmptyStopDate: IKnmErknmFormSuspensionEvent[] =
    suspensionEvents?.filter(item => !item.isInitStopDateFilled) || [];

  const isEnable =
    isSupervisor && (isKnmStatusTypeCarriedOut || isKnmStatusTypeWaitingCarryOut) && !isNeedActSaveOrSign;
  const isAddEnable =
    isEnable &&
    filteredEventOptions.length > 0 &&
    ((isValid412 && isKindDP) || suspensionEventsWithEmptyStopDate.length === 0);

  const suspensionEventsFieldName = 'suspensionEvents' as keyof IKnmForm;

  const addField = useCallback(() => {
    const field: IKnmErknmFormSuspensionEvent = { isNew: true };

    const acts = formRef?.current?.getFieldState('acts')?.value;
    const foundAct = acts?.find(i => i.isNew || !i.signed);

    if (foundAct) {
      return dispatch(
        addNoticeAction({
          renderContent: foundAct?.isNew
            ? ADD_SUSPENSION_ERROR_BY_NOT_FILLED_ACT
            : ADD_SUSPENSION_ERROR_BY_NOT_SIGNED_ACT,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.ACT_ERRORS,
        }),
      );
    }

    push && push(suspensionEventsFieldName, field);
  }, [push, formRef]);

  const removeField = useCallback(
    index => {
      remove && remove(suspensionEventsFieldName, index);
    },
    [remove],
  );

  return (
    <FieldBlock
      label="Сведения о приостановлении срока проведения КНМ"
      button={
        isSupervisor ? (
          <Button
            className="shared-btn-add"
            text="Добавить"
            type="button"
            variant="link"
            color="primary"
            onClick={addField}
            disabled={!isAddEnable || isRsnCngPPXXII}
          />
        ) : undefined
      }
      className="shared-field"
    >
      <FieldArray name={suspensionEventsFieldName}>
        {({ fields }) => {
          if (!fields.length) {
            return null;
          }

          return fields.map((name, index) => {
            const fieldValue: IKnmErknmFormSuspensionEvent = fields.value[index];

            return (
              <div key={name} className={styles.Collapse} id={getSuspensionEventBlockId(`${index}`)}>
                <ErknmSuspensionEventItem
                  name={name}
                  parentFieldName={suspensionEventsFieldName}
                  values={fieldValue}
                  index={index}
                  isKindDP={isKindDP}
                  isEnable={isEnable}
                  eventOptions={filteredEventOptions}
                  remove={removeField}
                  isValid412={isValid412}
                />
              </div>
            );
          });
        }}
      </FieldArray>
    </FieldBlock>
  );
};

const mapStateToProps = (state: IRootState): IErknmSuspensionEventsStoreProps => ({
  formRef: getKnmFormRef(state),
  isSupervisor: getUserRoleIsSupervisor(state),
  isKnmStatusTypeWaitingCarryOut: getKnmIsErknmStatusTypeWaitingCarryOut(state),
  isKnmStatusTypeCarriedOut: getKnmIsErknmStatusTypeCarriedOut(state),
  settings: getAdminFetchSettings(state),
  isNeedActSaveOrSign: getKnmIsNeedActSaveOrSign(state),
  isRsnCngPPXXII: getIsRsnCngPPXXII(state),
});

export const ErknmSuspensionEvents = connect(mapStateToProps)(ErknmSuspensionEventsWithoutStore);
